import React, { useEffect, useMemo, useRef, useState } from "react";

interface RichTextEditorProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    minHeight?: number;
}

const COLOR_PRESETS = [
    "#ecf2ff",
    "#cbd5e1",
    "#94a3b8",
    "#6d7cff",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#22c55e",
    "#38bdf8",
    "#f97316",
    "#e879f9",
];

const RichTextEditor: React.FC<RichTextEditorProps> = ({
                                                           label,
                                                           value,
                                                           onChange,
                                                           minHeight = 180,
                                                       }) => {
    const editorRef = useRef<HTMLDivElement | null>(null);
    const [mode, setMode] = useState<"visual" | "html">("visual");
    const [htmlValue, setHtmlValue] = useState(value);
    const [selectedColor, setSelectedColor] = useState("#ecf2ff");

    useEffect(() => {
        setHtmlValue(value);

        if (!editorRef.current) {
            return;
        }

        if (editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value;
        }
    }, [value]);

    const normalizedValue = useMemo(() => {
        return value.trim();
    }, [value]);

    const syncEditorValue = () => {
        const nextValue = editorRef.current?.innerHTML ?? "";
        setHtmlValue(nextValue);
        onChange(nextValue);
    };

    const executeCommand = (command: string, commandValue?: string) => {
        editorRef.current?.focus();
        document.execCommand(command, false, commandValue);
        syncEditorValue();
    };

    const handleCreateLink = () => {
        const url = window.prompt("Введите URL ссылки");

        if (!url) {
            return;
        }

        executeCommand("createLink", url);
    };

    const handleApplyColor = (color: string) => {
        setSelectedColor(color);
        executeCommand("styleWithCSS", "true");
        executeCommand("foreColor", color);
    };

    const handleModeChange = (nextMode: "visual" | "html") => {
        if (nextMode === "visual") {
            onChange(htmlValue);

            window.requestAnimationFrame(() => {
                if (editorRef.current) {
                    editorRef.current.innerHTML = htmlValue;
                }
            });
        }

        setMode(nextMode);
    };

    const handleHtmlChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const nextValue = event.target.value;
        setHtmlValue(nextValue);
        onChange(nextValue);
    };

    return (
        <div className="rich-editor-field">
            <div className="rich-editor-topline">
                <span className="ui-label">{label}</span>

                <div className="rich-editor-mode-switch">
                    <button
                        type="button"
                        className={`rich-editor-mode-button ${mode === "visual" ? "rich-editor-mode-button-active" : ""}`}
                        onClick={() => handleModeChange("visual")}
                    >
                        Visual
                    </button>

                    <button
                        type="button"
                        className={`rich-editor-mode-button ${mode === "html" ? "rich-editor-mode-button-active" : ""}`}
                        onClick={() => handleModeChange("html")}
                    >
                        HTML
                    </button>
                </div>
            </div>

            <div className="rich-editor">
                <div className="rich-editor-toolbar">
                    <button
                        type="button"
                        className="rich-editor-tool"
                        onClick={() => executeCommand("bold")}
                        disabled={mode === "html"}
                    >
                        B
                    </button>

                    <button
                        type="button"
                        className="rich-editor-tool"
                        onClick={() => executeCommand("italic")}
                        disabled={mode === "html"}
                    >
                        I
                    </button>

                    <button
                        type="button"
                        className="rich-editor-tool"
                        onClick={() => executeCommand("underline")}
                        disabled={mode === "html"}
                    >
                        U
                    </button>

                    <button
                        type="button"
                        className="rich-editor-tool"
                        onClick={() => executeCommand("formatBlock", "<h3>")}
                        disabled={mode === "html"}
                    >
                        H
                    </button>

                    <button
                        type="button"
                        className="rich-editor-tool"
                        onClick={() => executeCommand("formatBlock", "<p>")}
                        disabled={mode === "html"}
                    >
                        P
                    </button>

                    <button
                        type="button"
                        className="rich-editor-tool"
                        onClick={() => executeCommand("insertUnorderedList")}
                        disabled={mode === "html"}
                    >
                        • List
                    </button>

                    <button
                        type="button"
                        className="rich-editor-tool"
                        onClick={() => executeCommand("insertOrderedList")}
                        disabled={mode === "html"}
                    >
                        1. List
                    </button>

                    <button
                        type="button"
                        className="rich-editor-tool"
                        onClick={handleCreateLink}
                        disabled={mode === "html"}
                    >
                        Link
                    </button>

                    <button
                        type="button"
                        className="rich-editor-tool"
                        onClick={() => executeCommand("unlink")}
                        disabled={mode === "html"}
                    >
                        Unlink
                    </button>

                    <button
                        type="button"
                        className="rich-editor-tool"
                        onClick={() => executeCommand("removeFormat")}
                        disabled={mode === "html"}
                    >
                        Clear
                    </button>

                    <div className="rich-editor-color-group">
                        <span className="rich-editor-color-label">Color</span>

                        <input
                            type="color"
                            className="rich-editor-color-input"
                            value={selectedColor}
                            onChange={(event) => handleApplyColor(event.target.value)}
                            disabled={mode === "html"}
                            aria-label="Выбрать цвет текста"
                        />

                        <div className="rich-editor-swatches">
                            {COLOR_PRESETS.map((color) => (
                                <button
                                    key={color}
                                    type="button"
                                    className={`rich-editor-swatch ${selectedColor === color ? "rich-editor-swatch-active" : ""}`}
                                    style={{ backgroundColor: color }}
                                    onClick={() => handleApplyColor(color)}
                                    disabled={mode === "html"}
                                    aria-label={`Цвет ${color}`}
                                    title={color}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {mode === "visual" ? (
                    <div
                        ref={editorRef}
                        className="rich-editor-content"
                        style={{ minHeight }}
                        contentEditable
                        suppressContentEditableWarning
                        onInput={syncEditorValue}
                    />
                ) : (
                    <textarea
                        className="rich-editor-html"
                        style={{ minHeight }}
                        value={htmlValue}
                        onChange={handleHtmlChange}
                        spellCheck={false}
                    />
                )}
            </div>

            {normalizedValue ? (
                <div className="rich-editor-footer">
          <span className="rich-editor-footer-text">
            Текущий контент сохранён как HTML.
          </span>
                </div>
            ) : null}
        </div>
    );
};

export default RichTextEditor;
